import { Component, Input, OnInit, Inject, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { QuestionControlService } from './question-control.service';
import * as mcs from '../mcs';

@Component({
  selector: 'dynamic-form',
  encapsulation: ViewEncapsulation.None,
  template: `
    <div *ngIf="form">
      <div id="error-banner" class="banner-error" [class.banner-show]="isNotValid()">
        <b>{{getErrorCount() === 1 ? 'There is 1 error.' : 'There are ' + getErrorCount() + ' errors.'}}</b>
        <button aria-label="Close" class="close" type="button" (click)="closeError()">
          <span>×</span>
        </button>
      </div>
      <form (ngSubmit)="onSubmit()" [formGroup]="form" [id]="template.key" [autocomplete]="autocomplete">
        <div *ngFor="let group of groups; let i = index">
          <div *ngIf="!emptyGroup(group)" [attr.data-index]="i" class="row">
            <div *ngFor="let question of group" [class]="question.column">
              <df-question [question]="question" [form]="form" [template]="template" [prefill]="prefill"></df-question>
            </div>
          </div>
        </div>
        <div>
          <span *ngIf="template.backButton.show" class="left-align left-float">
            <button *ngIf="!showReviewButton()" class="btn btn-primary btn-sbla btn-back" type="button" (click)="prev()" id="dynamic-form-back" [attr.disabled]="loading || loadingBack">
              <span *ngIf="loadingBack" class="req-loader"></span>
              <span *ngIf="!loadingBack">{{template.backButton.text}}</span>
            </button>
            <button *ngIf="showReviewButton()"  class="btn btn-primary btn-sbla btn-back" type="button" (click)="reviewWithoutChanges()" id="dynamic-form-back">Cancel Changes</button>
          </span>
          <span class="right-align right-float">
            <button *ngIf="!showReviewButton()" class="btn btn-primary btn-sbla inline-block" type="submit" id="dynamic-form-submit" [attr.disabled]="loading || loadingBack">
              <span *ngIf="loading" class="req-loader"></span>
              <span *ngIf="!loading">{{template.btnTxt}}</span>
            </button>
            <button *ngIf="showReviewButton()" class="btn btn-primary btn-sbla inline-block" type="button" (click)="review()" id="dynamic-form-review" [attr.disabled]="loading || loadingBack">
              <span *ngIf="loading" class="req-loader"></span>
              <span *ngIf="!loading">Save Changes</span>
            </button>
          </span>
        </div>
      </form>
    </div>
  `,
  providers: [QuestionControlService],
  styles: [`
    label {
      font-weight: 200;
    }

    .form-control {
      font-weight: 200;
      box-shadow: none !important;
      color: $light-grey;
    }

    .help-block {
      font-size: 12px;
    }

    .has-error .control-label {
      color: #cf262a;
    }

    .has-error .form-control {
      background: $error-background;
      border-color: #cf262a;
    }

    .has-error .radio-option label {
      color: #cf262a;
    }

    .needs-input:before {
      position: absolute;
      left: 0;
      height: 100%;
      content: " ";
      border-left: solid 1px #0ea218;
    }

    .needs-input .form-control {
      background-color: $needs-input-background;
      border-color: #26a729;
    }

    .needs-input .radio-option label{
      color: #26a729;
    }

    &.banner-show {
      top: 0 !important;
    }

    .banner-error {
      transition: 0.5s;
      position: fixed;
      top: -90px;
      left: 0;
      width: 100%;
      padding: 30px;
      z-index: 10000;
      background-color: rgba( #cf262a, .9 );
      color: white;
      text-align: center;
    }

    .banner-error .close {
      color: white;
      opacity: 1;
    }

    .errorMessage {
      color: #cf262a;
    }
  `]
})

export class DynamicFormComponent implements OnInit {
  @Input() template: any = {};
  @Input() components: any;
  @Input() prefill: any = {};
  @Input() submit: Function;
  @Input() options: any;

  form: FormGroup = new FormGroup({});
  submitted: boolean;
  oldTemplate: any = {};
  groups: any = []; // Groups for display only
  oldPrefill: any = {};
  autocomplete: string = 'off';
  loading: any; // Button loading animations
  loadingBack: any;

  constructor(
    @Inject(QuestionControlService) public qcs: QuestionControlService
  ) {}

  showReviewButton() {
    return false;
  }

  ngOnInit() {
    this.template = mcs.process(this.template, this.components);
    this.form = this.qcs.toFormGroup(this.template, this.prefill);
    this.group(this.template);
  }

  ngDoCheck() {
    // Update form once prefill comes in
    if (Object.keys(this.prefill) && Object.keys(this.oldPrefill) &&
        Object.keys(this.prefill).length !== Object.keys(this.oldPrefill).length) {
      this.oldPrefill = JSON.parse(JSON.stringify(this.prefill));
      Object.keys(this.form.controls).forEach(control => {
        if (this.prefill.hasOwnProperty(control)) {
          this.form.controls[control].setValue(this.prefill[control]);
        }
      });
    }
    // Update groups for display on change
    if (this.oldTemplate.questions &&
      this.oldTemplate.questions.length !== this.template.questions.length) {
      this.group(this.template);
    }
    this.oldTemplate = JSON.parse(JSON.stringify(this.template));
  }

  group(template) {
    // organize questions into groups for row display
    let groupId = 0;
    this.groups = [];
    template.questions.forEach((question) => {
      // question = JSON.parse(JSON.stringify(question));

      // apply range of options
      if (question.tag === 'dropdown' && question.options[0]) {
        const firstOpt = question.options[0];
        switch (firstOpt.type) {
          case 'range':
            question.options = this.generateRange(firstOpt.min, firstOpt.max);
            break;
          default:
            break;
        }
      }

      let dependents = question.dependents;
      if (dependents) {
        const dependentKeys = Object.keys(dependents);
        for (let i = 0; i < dependentKeys.length; i++) {
          if (dependents[dependentKeys[i]].options) {
            const dependentOpts = dependents[dependentKeys[i]].options;
            dependentOpts.forEach((option, optionIdx) => {
              const dependentOpt = option.value[0];
              if (option.value[0])
                switch (dependentOpt.type) {
                  case 'range':
                    dependents[dependentKeys[i]].options[optionIdx].value = this.generateRange(
                      dependentOpt.min, dependentOpt.max
                    );
                    break;
                  default:
                    break;
                }
            });
          }
        }
      }

      if (!this.groups[groupId]) this.groups[groupId] = [];
      this.groups[groupId].push(question);
      if (question.rowEnd) groupId++;
    });
  }

  isNotValid() {
    return !this.form.valid && this.form.touched && this.submitted;
  }

  emptyGroup(group) {
    let isEmpty = true;
    group.forEach((groupItem) => {
      if (groupItem.showField !== false) isEmpty = false;
    });
    return isEmpty;
  }

  closeError() {
    this.submitted = false;
  }

  getErrorCount() {
    let numErrors = 0;
    Object.keys(this.form.controls).forEach(control => {
      let currControl = this.form.controls[control];
      if (currControl.valid === false && currControl.status !== 'DISABLED') {
        numErrors += 1;
      }
    });
    return numErrors;
  }

  onSubmit() {
    if (this.form.valid) {
      this.submit ? this.submit(this.form.value) : console.log(this.form.value);
    } else {
      Object.keys(this.form.controls).forEach(control => {
        this.form.controls[control].markAsTouched();
      });
      this.submitted = true;
    }
  }

  generateRange(min, max) {
    let result = [];
    let arrFn = max === 'currentYear' ? 'unshift' : 'push';

    if (max === 'currentYear') max = new Date().getFullYear();
    for (let i = min; i <= max; i++) {
      if (i < 10) i = '0' + i;
      result[arrFn]({ 'key': i, 'value': i });
    }

    return result;
  }

  prev() { }
  review() { }
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
